// HU-43 · Lo único que ve un especialista invitado al entrar: los casos a los que lo
// invitaron, y si puede abrirlos (invitación vigente y caso no cerrado, C4/C5). Abrir un
// caso lo lleva a la evaluación en SOLO LECTURA, donde aporta pero no decide (C7).

import { useEffect, useState } from 'react';
import { FolderOpen, Inbox, Lock } from 'lucide-react';
import { Alert, Button, Card, EmptyState, Mono, PageHeader, StatusBadge } from '../components/ui';
import Bandera from '../components/Banderas';
import { misInvitaciones } from '../api/evaluacion';
import type { InvitacionPropia } from '../api/evaluacion';
import { aFallo } from '../api/cliente';
import { comoFechaDeInstante } from '../lib/fechas';
import { useZonaDelPais } from '../lib/CatalogosContexto';
import type { Fallo } from '../api/cliente';

export default function MisInvitaciones({ onAbrir }: { onAbrir: (casoId: string) => void }) {
  const [invitaciones, setInvitaciones] = useState<InvitacionPropia[] | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  // DD/MM/AAAA en toda la interfaz (25-sep-2026); RN-82: el día, en la zona del país de cada caso.
  const zonaDe = useZonaDelPais();

  useEffect(() => {
    let vivo = true;
    misInvitaciones().then((i) => { if (vivo) setInvitaciones(i); }).catch((e) => { if (vivo) setFallo(aFallo(e)); });
    return () => { vivo = false; };
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="Especialista invitado · Mis invitaciones"
        hu="HU-43 · RN-31"
        title="Casos a los que te invitaron"
        subtitle="Acceso de solo lectura y solo a estos casos. Puedes dejar observaciones y adjuntar documentos de sustento."
        icon={Inbox}
      />
      {fallo && <Alert tone="danger" title="No se pudieron cargar tus invitaciones" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
      {invitaciones && invitaciones.length === 0 && (
        <Card pad={false}>
          <EmptyState icon={Inbox} title="Todavía no te han invitado a ningún caso"
            description="Cuando el Comité te invite recibirás un correo con el enlace." />
        </Card>
      )}
      {invitaciones && invitaciones.length > 0 && (
        <Card pad={false}>
          <ul className="divide-y divide-hairline" aria-label="Mis invitaciones">
            {invitaciones.map((i) => (
              <li key={i.invitacion_id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-body">
                <Bandera cod={i.pais_evento} />
                <Mono className="font-semibold">{i.numero ?? i.caso_id.slice(0, 8)}</Mono>
                <span className="text-ink-soft">invitado por {i.invitado_por} · {comoFechaDeInstante(i.invitado_en, zonaDe(i.pais_evento))}</span>
                {i.puede_abrirlo
                  ? <StatusBadge tone="success">Acceso vigente</StatusBadge>
                  : <StatusBadge tone="neutral" icon={Lock}>{i.vigente ? 'Caso cerrado' : 'Invitación retirada'}</StatusBadge>}
                <span className="ml-auto">
                  <Button size="sm" variant="secondary" icon={FolderOpen} disabled={!i.puede_abrirlo}
                    onClick={() => onAbrir(i.caso_id)} aria-label={`Abrir ${i.numero ?? 'el caso'}`}>
                    Abrir en solo lectura
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
